import React, { useState, useRef } from 'react'
import { ProfileGallery, GalleryItem, ProductCollectionEntry, ReviewPortfolioEntry } from '../types'

interface ProfileGalleriesProps {
  galleries: ProfileGallery[]
  productCollection: ProductCollectionEntry[]
  reviewPortfolio: ReviewPortfolioEntry[]
  isOwnProfile: boolean
  onUpdateGallery: (galleryId: string, gallery: Partial<ProfileGallery>) => void
  onCreateGallery: (gallery: Omit<ProfileGallery, 'id'>) => void
  onDeleteGallery: (galleryId: string) => void
}

const ProfileGalleries: React.FC<ProfileGalleriesProps> = ({
  galleries,
  productCollection,
  reviewPortfolio,
  isOwnProfile,
  onUpdateGallery,
  onCreateGallery,
  onDeleteGallery
}) => {
  const [activeGallery, setActiveGallery] = useState<string>(galleries[0]?.id || '')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [newGallery, setNewGallery] = useState({
    name: '',
    description: '',
    type: 'photos' as ProfileGallery['type'],
    isPublic: true
  })

  const galleryTypes = [
    { id: 'photos', name: 'Photo Gallery', icon: '📷', description: 'Collection of images' },
    { id: 'reviews', name: 'Review Showcase', icon: '⭐', description: 'Featured reviews' },
    { id: 'products', name: 'Product Collection', icon: '💊', description: '7OH tabs, powders & MIT extracts' },
    { id: 'achievements', name: 'Achievements', icon: '🏆', description: 'Badges and milestones' },
    { id: 'custom', name: 'Custom Gallery', icon: '🎨', description: 'Mixed content type' }
  ]

  const activeGalleryData = galleries.find(g => g.id === activeGallery)

  const handleCreateGallery = () => {
    onCreateGallery({
      ...newGallery,
      items: [],
      createdDate: new Date(),
      lastUpdated: new Date(),
      viewCount: 0,
      order: galleries.length
    })
    setShowCreateModal(false)
    setNewGallery({ name: '', description: '', type: 'photos', isPublic: true })
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && activeGalleryData) {
      Array.from(files).forEach((file, index) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const newItem: GalleryItem = {
            id: `item_${Date.now()}_${index}`,
            type: file.type.startsWith('video') ? 'video' : 'image',
            url: e.target?.result as string,
            title: file.name,
            description: '',
            dateAdded: new Date(),
            order: activeGalleryData.items.length + index,
            tags: []
          }
          
          onUpdateGallery(activeGalleryData.id, {
            items: [...activeGalleryData.items, newItem],
            lastUpdated: new Date()
          })
        }
        reader.readAsDataURL(file)
      })
    }
  }

  const renderGalleryItem = (item: GalleryItem) => {
    return (
      <div 
        key={item.id} 
        className="gallery-item"
        onClick={() => setSelectedItem(item)}
      >
        <div className="item-preview">
          {item.type === 'image' && item.url && (
            <img src={item.url} alt={item.title} />
          )}
          {item.type === 'video' && item.url && (
            <video src={item.url} poster={item.thumbnail} />
          )}
          {item.type === 'link' && (
            <div className="link-preview">
              <div className="link-icon">🔗</div>
              <div className="link-title">{item.title}</div>
            </div>
          )}
          {item.type === 'review' && (
            <div className="review-preview">
              <div className="review-icon">⭐</div>
              <div className="review-title">{item.title}</div>
            </div>
          )}
          {item.type === 'product' && (
            <div className="product-preview">
              <div className="product-icon">💊</div>
              <div className="product-name">{item.title}</div>
            </div>
          )}
        </div>
        
        <div className="item-info">
          <h4>{item.title}</h4>
          {item.description && <p>{item.description}</p>}
          <div className="item-meta">
            <span>{item.dateAdded.toLocaleDateString()}</span>
            {item.tags.length > 0 && (
              <div className="item-tags">
                {item.tags.slice(0, 2).map(tag => (
                  <span key={tag} className="tag">#{tag}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {isOwnProfile && (
          <div className="item-actions">
            <button className="edit-btn" title="Edit">✏️</button>
            <button className="delete-btn" title="Delete">🗑️</button>
          </div>
        )}
      </div>
    )
  }

  const renderProductCollection = () => {
    const favorites = productCollection.filter(product => product.isFavorite)
    const wishlist = productCollection.filter(product => product.isWishlist)
    const tried = productCollection.filter(product => !product.isFavorite && !product.isWishlist)
    
    const sevenOhTabs = productCollection.filter(p => p.type === '7oh-tabs')
    const sevenOhPowders = productCollection.filter(p => p.type === '7oh-powder')
    const mitExtracts = productCollection.filter(p => p.type === 'mit-extract')

    return (
      <div className="product-collection">
        <div className="collection-stats">
          <div className="stat-card">
            <div className="stat-number">{productCollection.length}</div>
            <div className="stat-label">Total Products</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{favorites.length}</div>
            <div className="stat-label">Favorites</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{wishlist.length}</div>
            <div className="stat-label">Wishlist</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{new Set(productCollection.map(p => p.vendor)).size}</div>
            <div className="stat-label">Vendors</div>
          </div>
        </div>

        <div className="product-type-breakdown">
          <div className="breakdown-stat">
            <div className="breakdown-icon">💊</div>
            <div className="breakdown-info">
              <div className="breakdown-number">{sevenOhTabs.length}</div>
              <div className="breakdown-label">7OH Tablets</div>
            </div>
          </div>
          <div className="breakdown-stat">
            <div className="breakdown-icon">🧪</div>
            <div className="breakdown-info">
              <div className="breakdown-number">{sevenOhPowders.length}</div>
              <div className="breakdown-label">7OH Powders</div>
            </div>
          </div>
          <div className="breakdown-stat">
            <div className="breakdown-icon">⚗️</div>
            <div className="breakdown-info">
              <div className="breakdown-number">{mitExtracts.length}</div>
              <div className="breakdown-label">MIT Extracts</div>
            </div>
          </div>
        </div>

        <div className="collection-sections">
          {favorites.length > 0 && (
            <div className="collection-section">
              <h4>⭐ Favorites</h4>
              <div className="product-grid">
                {favorites.map(product => (
                  <div key={product.id} className="product-card favorite">
                    <div className="product-header">
                      <h5>{product.productName}</h5>
                      <div className="product-rating">
                        {'★'.repeat(product.rating)}{'☆'.repeat(5-product.rating)}
                      </div>
                    </div>
                    <div className="product-vendor">{product.vendor}</div>
                    <div className="product-type">
                      {product.type === '7oh-tabs' && '💊 7OH Tablets'}
                      {product.type === '7oh-powder' && '🧪 7OH Powder'}
                      {product.type === 'mit-extract' && '⚗️ MIT Extract'}
                    </div>
                    <div className="product-potency">{product.potency}</div>
                    <div className="product-effects">
                      {product.effects.slice(0, 3).map(effect => (
                        <span key={effect} className="effect-tag">{effect}</span>
                      ))}
                    </div>
                    {product.labResults && (
                      <div className="lab-results">
                        <div className="lab-badge">🧪 Lab Tested</div>
                        <div className="lab-info">
                          7OH: {product.labResults['7oh']}%
                        </div>
                      </div>
                    )}
                    {product.images.length > 0 && (
                      <div className="product-image">
                        <img src={product.images[0]} alt={product.productName} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {wishlist.length > 0 && (
            <div className="collection-section">
              <h4>💭 Wishlist</h4>
              <div className="product-grid">
                {wishlist.map(product => (
                  <div key={product.id} className="product-card wishlist">
                    <div className="product-header">
                      <h5>{product.productName}</h5>
                      <div className="wishlist-badge">💭</div>
                    </div>
                    <div className="product-vendor">{product.vendor}</div>
                    <div className="product-type">
                      {product.type === '7oh-tabs' && '💊 7OH Tablets'}
                      {product.type === '7oh-powder' && '🧪 7OH Powder'}
                      {product.type === 'mit-extract' && '⚗️ MIT Extract'}
                    </div>
                    <div className="product-potency">{product.potency}</div>
                    <div className="product-notes">{product.notes}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="collection-section">
            <h4>💊 All Products ({tried.length})</h4>
            <div className="product-grid">
              {tried.map(product => (
                <div key={product.id} className="product-card">
                  <div className="product-header">
                    <h5>{product.productName}</h5>
                    <div className="product-rating">
                      {'★'.repeat(product.rating)}{'☆'.repeat(5-product.rating)}
                    </div>
                  </div>
                  <div className="product-vendor">{product.vendor}</div>
                  <div className="product-type">
                    {product.type === '7oh-tabs' && '💊 7OH Tablets'}
                    {product.type === '7oh-powder' && '🧪 7OH Powder'}
                    {product.type === 'mit-extract' && '⚗️ MIT Extract'}
                  </div>
                  <div className="product-potency">{product.potency}</div>
                  <div className="product-effects">
                    {product.effects.slice(0, 2).map(effect => (
                      <span key={effect} className="effect-tag">{effect}</span>
                    ))}
                  </div>
                  <div className="product-meta">
                    <span>{product.dateAdded.toLocaleDateString()}</span>
                    <span>{product.purchaseHistory.length} purchases</span>
                    {product.labResults && <span>🧪 Lab Tested</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderReviewPortfolio = () => {
    const featured = reviewPortfolio.filter(review => review.isFeatured)
    const categories = {
      detailed: reviewPortfolio.filter(r => r.category === 'detailed'),
      helpful: reviewPortfolio.filter(r => r.category === 'helpful'),
      recent: reviewPortfolio.filter(r => r.category === 'recent'),
      controversial: reviewPortfolio.filter(r => r.category === 'controversial')
    }

    return (
      <div className="review-portfolio">
        <div className="portfolio-stats">
          <div className="stat-card">
            <div className="stat-number">{reviewPortfolio.length}</div>
            <div className="stat-label">Total Reviews</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{featured.length}</div>
            <div className="stat-label">Featured</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{reviewPortfolio.reduce((sum, r) => sum + r.helpfulVotes, 0)}</div>
            <div className="stat-label">Helpful Votes</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{(reviewPortfolio.reduce((sum, r) => sum + r.rating, 0) / reviewPortfolio.length).toFixed(1)}</div>
            <div className="stat-label">Avg Rating</div>
          </div>
        </div>

        {featured.length > 0 && (
          <div className="portfolio-section">
            <h4>🌟 Featured Reviews</h4>
            <div className="review-grid">
              {featured.map(review => (
                <div key={review.reviewId} className="review-card featured">
                  <div className="review-header">
                    <h5>{review.productName}</h5>
                    <div className="review-rating">
                      {'★'.repeat(review.rating)}{'☆'.repeat(5-review.rating)}
                    </div>
                  </div>
                  <div className="review-product-type">
                    {review.productType === '7oh-tabs' && '💊 7OH Tablets'}
                    {review.productType === '7oh-powder' && '🧪 7OH Powder'}
                    {review.productType === 'mit-extract' && '⚗️ MIT Extract'}
                  </div>
                  <div className="review-potency">{review.potency}</div>
                  <div className="review-vendor">{review.vendorName}</div>
                  <div className="review-stats">
                    <span>👍 {review.helpfulVotes}</span>
                    <span>👁️ {review.views}</span>
                    <span>💬 {review.comments}</span>
                  </div>
                  <div className="review-date">{review.dateWritten.toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="portfolio-categories">
          {Object.entries(categories).map(([category, reviews]) => (
            reviews.length > 0 && (
              <div key={category} className="portfolio-section">
                <h4>
                  {category === 'detailed' && '📝 Detailed Reviews'}
                  {category === 'helpful' && '👍 Most Helpful'}
                  {category === 'recent' && '🕒 Recent Reviews'}
                  {category === 'controversial' && '🔥 Controversial'}
                  {' '}({reviews.length})
                </h4>
                <div className="review-grid">
                  {reviews.slice(0, 6).map(review => (
                    <div key={review.reviewId} className={`review-card ${category}`}>
                      <div className="review-header">
                        <h5>{review.productName}</h5>
                        <div className="review-rating">
                          {'★'.repeat(review.rating)}{'☆'.repeat(5-review.rating)}
                        </div>
                      </div>
                      <div className="review-product-type">
                        {review.productType === '7oh-tabs' && '💊 7OH Tablets'}
                        {review.productType === '7oh-powder' && '🧪 7OH Powder'}
                        {review.productType === 'mit-extract' && '⚗️ MIT Extract'}
                      </div>
                      <div className="review-potency">{review.potency}</div>
                      <div className="review-vendor">{review.vendorName}</div>
                      <div className="review-stats">
                        <span>👍 {review.helpfulVotes}</span>
                        <span>👁️ {review.views}</span>
                      </div>
                      <div className="review-date">{review.dateWritten.toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="profile-galleries">
      <div className="galleries-header">
        <h3>🖼️ Galleries</h3>
        {isOwnProfile && (
          <button 
            className="create-gallery-btn"
            onClick={() => setShowCreateModal(true)}
          >
            ➕ Create Gallery
          </button>
        )}
      </div>

      <div className="gallery-tabs">
        {galleries.map(gallery => (
          <button
            key={gallery.id}
            className={`gallery-tab ${activeGallery === gallery.id ? 'active' : ''}`}
            onClick={() => setActiveGallery(gallery.id)}
          >
            {galleryTypes.find(t => t.id === gallery.type)?.icon} {gallery.name}
            {!gallery.isPublic && <span className="private-badge">🔒</span>}
          </button>
        ))}
      </div>

      <div className="gallery-content">
        {activeGalleryData && (
          <>
            <div className="gallery-info">
              <h4>{activeGalleryData.name}</h4>
              {activeGalleryData.description && <p>{activeGalleryData.description}</p>}
              <div className="gallery-meta">
                <span>{activeGalleryData.items.length} items</span>
                <span>{activeGalleryData.viewCount} views</span>
                <span>Updated {activeGalleryData.lastUpdated.toLocaleDateString()}</span>
              </div>
            </div>

            {isOwnProfile && activeGalleryData.type === 'photos' && (
              <div className="gallery-actions">
                <button onClick={() => fileInputRef.current?.click()}>
                  📤 Upload Images
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </div>
            )}

            <div className="gallery-items">
              {activeGalleryData.type === 'products' ? (
                renderProductCollection()
              ) : activeGalleryData.type === 'reviews' ? (
                renderReviewPortfolio()
              ) : (
                <div className="items-grid">
                  {activeGalleryData.items.map(renderGalleryItem)}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create Gallery Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🎨 Create New Gallery</h3>
              <button onClick={() => setShowCreateModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Gallery Name</label>
                <input
                  type="text"
                  value={newGallery.name}
                  onChange={(e) => setNewGallery(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter gallery name"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newGallery.description}
                  onChange={(e) => setNewGallery(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your gallery"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Gallery Type</label>
                <div className="gallery-type-grid">
                  {galleryTypes.map(type => (
                    <div
                      key={type.id}
                      className={`gallery-type-option ${newGallery.type === type.id ? 'selected' : ''}`}
                      onClick={() => setNewGallery(prev => ({ ...prev, type: type.id as any }))}
                    >
                      <div className="type-icon">{type.icon}</div>
                      <div className="type-name">{type.name}</div>
                      <div className="type-description">{type.description}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newGallery.isPublic}
                    onChange={(e) => setNewGallery(prev => ({ ...prev, isPublic: e.target.checked }))}
                  />
                  Make gallery public
                </label>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button 
                onClick={handleCreateGallery}
                disabled={!newGallery.name}
              >
                Create Gallery
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Detail Modal */}
      {selectedItem && (
        <div className="modal-overlay" onClick={() => setSelectedItem(null)}>
          <div className="modal-content item-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedItem.title}</h3>
              <button onClick={() => setSelectedItem(null)}>×</button>
            </div>

            <div className="item-detail-content">
              {selectedItem.type === 'image' && selectedItem.url && (
                <img src={selectedItem.url} alt={selectedItem.title} />
              )}
              {selectedItem.type === 'video' && selectedItem.url && (
                <video controls src={selectedItem.url} />
              )}
              
              {selectedItem.description && (
                <p className="item-description">{selectedItem.description}</p>
              )}
              
              <div className="item-details">
                <div className="detail-row">
                  <strong>Added:</strong> {selectedItem.dateAdded.toLocaleDateString()}
                </div>
                {selectedItem.tags.length > 0 && (
                  <div className="detail-row">
                    <strong>Tags:</strong>
                    <div className="tags">
                      {selectedItem.tags.map(tag => (
                        <span key={tag} className="tag">#{tag}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfileGalleries